<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Plus, ChevronDown, CalendarCheck } from 'lucide-vue-next'
import { useAuthStore } from '@/store/AuthStore'
import { useAppShellStore } from '@/store/AppShellStore'
import { useScheduleSettingsStore } from '@/store/ScheduleSettingsStore'
import { addDays, hhmm, parseDateKey, startOfWeek, toLocalDateKey } from '@/utils/date'
import { scheduleApi, studentsApi } from '@/api'
import type { ScheduleItem, StudentSummary, PlanningScheme } from '@/api'
import {
  AppButton,
  EmptyState,
  paletteForKey,
  paletteFromColour,
  type PaletteColour,
  type PaletteHue,
} from '@/design-system/components'
import PageHeader from '@/components/today/PageHeader.vue'
import NextLessonHero from '@/components/today/NextLessonHero.vue'
import DayTimeline, { type TimelineRow } from '@/components/today/DayTimeline.vue'
import BirthdaysWidget, { type BirthdayRow } from '@/components/today/BirthdaysWidget.vue'
import NeedsAttentionWidget, { type AttentionRow } from '@/components/today/NeedsAttentionWidget.vue'
import WeekAheadWidget from '@/components/today/WeekAheadWidget.vue'
import CreateSchemeModal from '@/components/modals/CreateSchemeModal.vue'
import { useAnalytics } from '@/composables/useAnalytics'

const router = useRouter()
const auth = useAuthStore()
const { capture } = useAnalytics()
const appShell = useAppShellStore()
const scheduleSettings = useScheduleSettingsStore()

const now = new Date()
const todayISO = toLocalDateKey(now)

// Week containing today, anchored on the school's week-start setting.
const weekDates = computed(() => {
  const start = startOfWeek(now, scheduleSettings.weekStartDay)
  return Array.from({ length: 7 }, (_, i) => addDays(start, i))
})

// ── State ────────────────────────────────────────────────────────────────────
const loading = ref(true)
const todayItems = ref<ScheduleItem[]>([])
const weekLessonCount = ref(0)
const weekAssignmentCount = ref(0)
const birthdays = ref<StudentSummary[]>([])

function resolveHue(colour?: string | null, classId?: string): PaletteHue {
  if (colour) return paletteFromColour(colour as PaletteColour)
  return paletteForKey(classId ?? '')
}

// ── Header copy ──────────────────────────────────────────────────────────────
const firstName = computed(() => (auth.userName || '').split(/\s+/)[0] || 'there')
const greeting = computed(() => {
  const h = now.getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
})
const headerEyebrow = computed(() =>
  now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }),
)
const dateRight = computed(() =>
  now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }),
)

// ── Timeline (lessons + events, time order) ──────────────────────────────────
interface PreparedItem extends TimelineRow {
  startDate: Date
  endDate: Date | null
  attendancePercent?: number | null
}

const prepared = computed<PreparedItem[]>(() => {
  return todayItems.value
    .filter((it) => it.type === 'lesson' || it.type === 'event')
    .map((it) => {
      const startDate = new Date(it.dateTime)
      const endDate = it.endDateTime ? new Date(it.endDateTime) : null
      const hue = resolveHue(it.classColour, it.classId)
      return {
        id: it.id,
        kind: it.type as 'lesson' | 'event',
        hue,
        start: hhmm(startDate),
        end: endDate ? hhmm(endDate) : null,
        className: it.className ?? (it.type === 'event' ? 'Event' : 'Lesson'),
        title: it.title ?? '',
        room: it.room,
        topic: null, // not provided by the schedule API yet
        planReady: undefined, // not provided by the schedule API yet
        isNext: false,
        classId: it.classId,
        startDate,
        endDate,
        attendancePercent: it.attendancePercent,
      }
    })
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
})

const heroItem = computed<PreparedItem | null>(() => {
  const lessons = prepared.value.filter((p) => p.kind === 'lesson')
  const upcoming = lessons.filter((p) => (p.endDate ?? p.startDate) >= now)
  return upcoming[0] ?? null
})

const heroStartsLabel = computed(() => {
  const h = heroItem.value
  if (!h) return ''
  if (h.startDate <= now && h.endDate && now <= h.endDate) return 'in progress'
  const mins = Math.round((h.startDate.getTime() - now.getTime()) / 60000)
  if (mins <= 0) return 'starting now'
  if (mins < 60) return `starts in ${mins} min`
  return `starts at ${hhmm(h.startDate)}`
})

const timelineRows = computed<TimelineRow[]>(() =>
  prepared.value.map((p) => ({ ...p, isNext: p.id === heroItem.value?.id })),
)

const lessonCountToday = computed(() => prepared.value.filter((p) => p.kind === 'lesson').length)
const eventCountToday = computed(() => prepared.value.filter((p) => p.kind === 'event').length)

const headerSub = computed(() => {
  const l = lessonCountToday.value
  const e = eventCountToday.value
  const lp = `${l} ${l === 1 ? 'lesson' : 'lessons'}`
  const ep = `${e} ${e === 1 ? 'event' : 'events'}`
  if (!l && !e) return 'Nothing scheduled today.'
  return `You have ${lp} and ${ep} today.`
})

// ── Birthdays this week ──────────────────────────────────────────────────────
const birthdayRows = computed<BirthdayRow[]>(() => {
  const rows: BirthdayRow[] = []
  for (const s of birthdays.value) {
    if (!s.dob) continue
    const dob = parseDateKey(s.dob)
    const match = weekDates.value.find(
      (d) => d.getMonth() === dob.getMonth() && d.getDate() === dob.getDate(),
    )
    if (!match) continue
    const cls = s.classes?.[0]
    rows.push({
      id: s.id,
      initials: `${s.firstName?.[0] ?? ''}${s.lastName?.[0] ?? ''}`.toUpperCase() || '?',
      hue: resolveHue(cls?.colour, cls?.id ?? s.id),
      name: `${s.firstName}${s.lastName ? ' ' + s.lastName : ''}`,
      age: match.getFullYear() - dob.getFullYear(),
      className: cls?.name ?? '',
      dayLabel: `${match.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase()} ${match.getDate()}`,
      isToday: toLocalDateKey(match) === todayISO,
      photoUrl: s.photoUrl,
    })
  }
  return rows.sort((a, b) => (a.isToday === b.isToday ? 0 : a.isToday ? -1 : 1))
})

// ── Needs attention (derived from today's lessons) ───────────────────────────
// Real signal available today: a past lesson whose register isn't taken yet
// (attendancePercent == null). Plan-backlog and to-grade need backend support.
const attentionRows = computed<AttentionRow[]>(() => {
  const rows: AttentionRow[] = []
  for (const p of prepared.value) {
    if (p.kind !== 'lesson' || !p.classId) continue
    const isPast = (p.endDate ?? p.startDate) < now
    if (isPast && (p.attendancePercent == null)) {
      rows.push({
        id: `reg-${p.id}`,
        kind: 'register',
        hue: p.hue,
        title: 'Register not taken',
        className: p.className,
        meta: p.start,
        route: `/classes/${p.classId}/lesson/${p.id}`,
      })
    }
  }
  return rows
})

// ── Week-ahead stats ─────────────────────────────────────────────────────────
const weekToGrade = ref<number | string>('—') // no to-grade endpoint yet

// ── Quick add ────────────────────────────────────────────────────────────────
const quickAddOpen = ref(false)
const showSchemeModal = ref(false)
const quickAddRef = ref<HTMLElement | null>(null)
function onClickOutside(e: MouseEvent) {
  if (quickAddRef.value && !quickAddRef.value.contains(e.target as Node)) quickAddOpen.value = false
}
function quickNewClass() {
  quickAddOpen.value = false
  router.push('/classes')
}
function quickNewScheme() {
  quickAddOpen.value = false
  showSchemeModal.value = true
}
function onSchemeCreated(scheme: PlanningScheme) {
  router.push(`/planning/${scheme.id}`)
}

// ── Routing ──────────────────────────────────────────────────────────────────
function openClass(classId: string) {
  // Opened from the Today schedule (not the ordered class list), so class_index
  // isn't meaningful here — the event still counts as an engagement signal.
  capture('class_opened')
  router.push(`/classes/${classId}`)
}
function heroTakeRegister() {
  if (heroItem.value?.classId) router.push(`/classes/${heroItem.value.classId}/lesson/${heroItem.value.id}`)
}
function heroOpenPlan() {
  if (heroItem.value?.classId) router.push(`/classes/${heroItem.value.classId}/lesson/${heroItem.value.id}`)
}
function heroOpenClass() {
  if (heroItem.value?.classId) openClass(heroItem.value.classId)
}

// ── Fetch ────────────────────────────────────────────────────────────────────
async function fetchData() {
  loading.value = true
  appShell.loadClasses()
  try {
    // Week range depends on the school's week-start setting
    if (!scheduleSettings.isLoaded) await scheduleSettings.fetchSettings()
    const week = weekDates.value
    const [todayRes, weekRes, bdays] = await Promise.all([
      scheduleApi.getSchedule({ from: todayISO, to: todayISO }),
      scheduleApi.getSchedule({ from: toLocalDateKey(week[0]!), to: toLocalDateKey(week[6]!) }),
      studentsApi.getUpcomingBirthdays().catch(() => [] as StudentSummary[]),
    ])
    todayItems.value = todayRes.items ?? []
    weekLessonCount.value = weekRes.lessonCount ?? 0
    weekAssignmentCount.value = weekRes.assignmentCount ?? 0
    birthdays.value = bdays ?? []
  } catch (err) {
    console.error('Failed to load Today:', err)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchData()
  document.addEventListener('click', onClickOutside)
})
onBeforeUnmount(() => document.removeEventListener('click', onClickOutside))
</script>

<template>
  <div class="today">
    <PageHeader :eyebrow="headerEyebrow" :title="`${greeting}, ${firstName}`" :sub="headerSub">
      <template #right>
        <div ref="quickAddRef" class="today__quickadd">
          <AppButton variant="secondary" size="md" @click="quickAddOpen = !quickAddOpen">
            <Plus :size="15" /> Quick add <ChevronDown :size="14" />
          </AppButton>
          <div v-if="quickAddOpen" class="today__qa-menu">
            <button type="button" class="today__qa-item" @click="quickNewClass">New class</button>
            <button type="button" class="today__qa-item" @click="quickNewScheme">
              New scheme of work
            </button>
          </div>
        </div>
      </template>
    </PageHeader>

    <div class="today__grid">
      <!-- spine -->
      <div class="today__spine">
        <div v-if="heroItem" class="today__hero">
          <NextLessonHero
            :hue="heroItem.hue"
            :class-name="heroItem.className"
            :starts-label="heroStartsLabel"
            :time-range="`${heroItem.start}${heroItem.end ? '–' + heroItem.end : ''}`"
            :room="heroItem.room"
            :title="heroItem.title"
            :topic="heroItem.topic"
            @take-register="heroTakeRegister"
            @open-plan="heroOpenPlan"
            @open-class="heroOpenClass"
          />
        </div>
        <div v-else-if="!loading" class="today__alldone">
          <span class="today__alldone-icon"><CalendarCheck :size="20" /></span>
          <div>
            <div class="today__alldone-title">
              {{ lessonCountToday ? 'No more lessons today' : 'Nothing scheduled today' }}
            </div>
            <div class="today__alldone-sub">Enjoy the breathing room.</div>
          </div>
        </div>

        <div class="today__timeline-head">
          <span class="today__eyebrow">Today · {{ prepared.length }} on the timetable</span>
          <span class="today__date">{{ dateRight }}</span>
        </div>

        <EmptyState
          v-if="!loading && !prepared.length"
          :icon="CalendarCheck"
          title="Nothing on today"
          body="No lessons or events on the timetable today. Enjoy the breathing room."
        />
        <DayTimeline v-else :rows="timelineRows" @open-class="openClass" />
      </div>

      <!-- widget rail -->
      <div class="today__rail">
        <BirthdaysWidget :rows="birthdayRows" @open-student="(id) => router.push(`/students/${id}`)" />
        <NeedsAttentionWidget
          :rows="attentionRows"
          @open="(route) => router.push(route)"
          @review-all="router.push('/schedule')"
        />
        <WeekAheadWidget
          :lessons="weekLessonCount"
          :due-in="weekAssignmentCount"
          :to-grade="weekToGrade"
          @open-schedule="router.push('/schedule')"
        />
      </div>
    </div>

    <CreateSchemeModal v-model="showSchemeModal" @created="onSchemeCreated" />
  </div>
</template>

<style scoped>
.today {
  max-width: 1320px;
  margin: 0 auto;
  padding: 26px 36px 56px;
  font-family: var(--font-sans);
}
.today__quickadd {
  position: relative;
}
.today__qa-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: var(--z-dropdown);
  width: 200px;
  background: var(--surface-raised);
  border: 1px solid var(--outline-variant);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-raise);
  padding: 6px;
}
.today__qa-item {
  display: block;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  text-align: left;
  font-family: var(--font-sans);
  font-size: 13.5px;
  color: var(--text-secondary);
  cursor: pointer;
}
.today__qa-item:hover {
  background: var(--ink-100);
}

.today__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 28px;
  align-items: start;
}
.today__hero {
  margin-bottom: 24px;
}
.today__alldone {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  margin-bottom: 24px;
  background: var(--surface);
  border-radius: 16px;
  box-shadow: var(--hairline-border);
}
.today__alldone-icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: var(--radius-md);
  background: var(--palette-indigo-soft);
  color: var(--palette-indigo-ink);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.today__alldone-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}
.today__alldone-sub {
  margin-top: 2px;
  font-size: 13.5px;
  color: var(--ink-500);
}
.today__timeline-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  padding-left: 2px;
}
.today__eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--ink-400);
}
.today__date {
  font-size: 12.5px;
  color: var(--ink-500);
}
.today__rail {
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 0;
}

@media (max-width: 1100px) {
  .today__grid {
    grid-template-columns: 1fr;
  }
  .today__rail {
    position: static;
  }
  .today {
    padding: 22px 20px 48px;
  }
}
</style>
